'use client'
import { useRef, useState, useCallback } from "react"
import { Button } from '@sarkiai/ui'
import { Mic } from "lucide-react"
import { motion, useAnimationControls } from "framer-motion"
import { cn } from '@sarkiai/utils'

interface AudioRecorderProps {
  isLoading?: boolean
  isStreaming?: boolean
  onRecorded: (blob: Blob) => void
  disabled?: boolean
}

export const AudioRecorder = ({ isLoading, isStreaming, onRecorded, disabled }: AudioRecorderProps) => {
  const controls = useAnimationControls()

  const [isRecording, setIsRecording] = useState(false)

  const chunks = useRef<BlobPart[]>([])
  const mediaStream = useRef<MediaStream | null>(null)
  const mediaRecorder = useRef<MediaRecorder | null>(null)

  const motionVariants = {
    recording: {
      scale: [null, 1.2],
      transition: {
        repeat: Infinity,
        duration: 0.8,
        repeatType: "reverse" as const,
      }
    },
    completed: {
      scale: 1,
      transition: {
        damping: 20,
        type: "spring" as const,
        stiffness: 260,
      }
    },
  }

  const startRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })

      mediaStream.current = stream
      mediaRecorder.current = new MediaRecorder(stream)

      mediaRecorder.current.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunks.current.push(e.data)
        }
      }

      mediaRecorder.current.onstop = () => {
        const blob = new Blob(chunks.current, { type: 'audio/webm' })
        onRecorded(blob)
        chunks.current = []
      }

      mediaRecorder.current.start()
      await controls.start("recording")
    } catch (error) {
      console.error('Error accessing microphone:', error)
    }
  }, [controls, onRecorded])

  const stopRecording = useCallback(() => {
    if (mediaRecorder.current && mediaRecorder.current.state === 'recording') {
      mediaRecorder.current.stop()
    }
    if (mediaStream.current) {
      mediaStream.current.getTracks().forEach((track) => {
        track.stop()
      })
    }
  }, [])

  const handleToggleRecording = useCallback(async () => {
    if (!isRecording) {
      setIsRecording(true)
      await startRecording()
    } else {
      setIsRecording(false)
      stopRecording()
      await controls.start("completed")
    }
  }, [isRecording, controls, startRecording, stopRecording])

  return (
    <motion.div
      initial={false}
      animate={controls}
      variants={motionVariants}
    >
      <Button
        type="button"
        variant="ghost"
        size="icon"
        onClick={handleToggleRecording}
        disabled={disabled || isLoading || isStreaming}
        className={cn(
          "h-8 w-8 sm:h-9 sm:w-9 rounded-full shrink-0 transition-colors",
          isRecording
            ? "bg-red-500 hover:bg-red-600 text-white"
            : "text-primary hover:text-primary/80"
        )}
        aria-label={isRecording ? "Arrêter l'enregistrement" : "Démarrer l'enregistrement vocal"}
        aria-pressed={isRecording}
      >
        <Mic className="h-4 w-4 sm:h-5 sm:w-5" aria-hidden="true" />
      </Button>
    </motion.div>
  )
}
